import { Directive, ElementRef, Input, NgZone, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';

/**
 * Cuenta hasta la cifra — el contador de `.dxc-stat__value`.
 *
 * La cifra destacada es el asunto de la pantalla, y aparece de golpe cuando el
 * backend contesta: el saldo salta del esqueleto al número y no queda nada que
 * mirar. Contar hasta él hace dos cosas que un número quieto no hace — dice que
 * la cifra ACABA de llegar, y lleva la mirada al sitio donde vive.
 *
 * ## Por qué es una directiva y no código de la vista
 *
 * Lo que se anima es la primitiva, no la pantalla: cualquier `.dxc-stat` —el
 * saldo de puntos hoy, el total facturado mañana— quiere lo mismo. En la vista
 * sería un `setInterval` que el siguiente que necesite un contador vuelve a
 * escribir, y con él vuelven las tres trampas que esto ya resuelve: el bucle
 * que sigue vivo después de destruir el componente, el ciclo de detección de
 * cambios en cada fotograma, y el usuario que pidió movimiento reducido.
 *
 * ## El reposo es el valor, no el final de la animación
 *
 * Si la animación no llega a correr —movimiento reducido, la pestaña en
 * segundo plano cuando el dato aterriza, un navegador sin `requestAnimationFrame`—
 * el elemento enseña la cifra final igualmente. Es la misma propiedad que §6
 * exige al punto verde del stepper: el dato no puede depender de que un
 * fotograma llegue.
 *
 * @example
 * <span class="dxc-stat__value" [dxcCountUp]="balance"></span>
 */
@Directive({
    standalone: true,
    selector: '[dxcCountUp]',
})
export class DxcCountUpDirective implements OnChanges, OnDestroy {

    /** La cifra de destino. Al cambiar, cuenta desde la que ya se ve. */
    @Input('dxcCountUp') value: number | null = null;

    /** Lo bastante para leerse como un conteo, lo bastante poco para no estorbar. */
    @Input() countUpDuration = 900;

    private frame = 0;
    private shown = 0;
    private readonly format = new Intl.NumberFormat('es-MX');

    constructor(private host: ElementRef<HTMLElement>, private zone: NgZone) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes['value']) return;

        const target = Number(this.value ?? 0);
        if (!Number.isFinite(target)) return;

        cancelAnimationFrame(this.frame);

        const from = this.shown;
        if (from === target || this.skipAnimation) {
            this.paint(target);
            return;
        }

        // Fuera de la zona: son ~50 fotogramas y ninguno cambia un dato de
        // Angular — dentro de la zona serían 50 ciclos de detección de cambios
        // para escribir un `textContent`.
        this.zone.runOutsideAngular(() => {
            const start = performance.now();
            const step = (now: number) => {
                const t = Math.min(1, (now - start) / this.countUpDuration);
                // Frenado sin rebote (§2): entra rápido y se posa en la cifra.
                const eased = 1 - Math.pow(1 - t, 3);
                this.paint(Math.round(from + (target - from) * eased));
                if (t < 1) this.frame = requestAnimationFrame(step);
            };
            this.frame = requestAnimationFrame(step);
        });
    }

    ngOnDestroy(): void {
        cancelAnimationFrame(this.frame);
    }

    private paint(n: number): void {
        this.shown = n;
        this.host.nativeElement.textContent = this.format.format(n);
    }

    /**
     * Tres motivos para no contar, y el tercero es el que se olvida: con la
     * pestaña oculta el reloj del documento no avanza y `requestAnimationFrame`
     * no llega nunca, así que la cifra se quedaría en el cero de salida hasta
     * que alguien volviera a mirar. La cifra es el dato; el conteo, un adorno
     * que solo se paga cuando hay quien lo vea.
     */
    private get skipAnimation(): boolean {
        if (typeof requestAnimationFrame !== 'function') return true;
        if (typeof document !== 'undefined' && document.hidden) return true;
        return typeof matchMedia === 'function'
            && matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
}
